<template lang="pug">
    layout.contentTransparent
        div.layoutWrapper
            v-layout(row wrap)
                v-flex(xs12)
                    div.sessionHeader

                        // Title
                        div.titleWrapper
                            h1(v-html="titleText")

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs12 sm4 md4 lg3)
                    div.c-form

                        // Search field
                        v-text-field.search-input(
                            append-icon="search"
                            outline
                            v-model="search"
                            :label="$t('fields.search')"
                        )

            // Horizontal line
            separator-component.overlay

            div.filters

                div.mr-3
                    // Instructor filter
                    filter-component.mr-4(
                        v-if="instructors && instructors.length && !isInstructor"
                        name="Instructor"
                        filterName="instructor"
                        :items="instructors"
                        :callbackFunction="instructorFiltersCallback"
                    )

            v-data-iterator(
                item-key="id"
                content-tag="v-layout"
                no-data-text="No videos available"
                row
                wrap
                :items="items"
                :rows-per-page-items="[2]"
                :pagination.sync="pagination"
                :total-items="total"
            )
                template(v-slot:item="props")
                    v-flex(xs12 sm6 md4 lg3 xl2)
                        meditation-component(:item="props.item" :duration="meditationDuration" :key="props.item.id")
</template>

<script>
    import Layout from '../../Layout/Layout';
    import { sessions } from '~api';
    import Button from '~components/Button';
    import ButtonIcon from '~components/ButtonIcon';
    import Filter from '~components/Filter';
    import Separator from '~components/Separator';
    import Status from '~components/Status';
    import Title from '~components/Title';
    import { mapActions, mapGetters } from 'vuex';
    import formattedDate from '~filters/FormattedDate';
    import formattedDateTime from '~filters/FormattedDateTime';
    import formattedFullName from '~filters/FormattedFullName';
    import formattedTime from '~filters/FormattedTime';
    import { managers } from '~api';
    import VueTypes from 'vue-types';

    export default {
        components: {
            Layout,
            MeditationComponent: () =>
                import('../../../../components/Meditation'),
            TitleComponent: Title,
            ButtonComponent: Button,
            ButtonIconComponent: ButtonIcon,
            StatusComponent: Status,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        filters: {
            formattedDate,
            formattedTime,
            formattedFullName,
            formattedDateTime
        },
        props: {
            instructor: VueTypes.object.def({}),
            duration: VueTypes.number.def(0)
        },
        data() {
            return {
                titleText: 'Meetings',
                filters: {},
                items: [],
                meditationDuration: this.duration,
                pagination: {},
                sortBy: null,
                orderBy: null,
                total: 0,
                rowsPerPage: [10],
                search: null,
                timeout: null,
                routeInitialized: false,
                isLoading: false,
                instructors: [],
                instructorFullName: ''
            };
        },

        computed: {
            ...mapGetters('auth', ['isInstructor']),
            ...mapGetters('auth', [
                'isAdmin',
                'isInstructor',
                'isMember',
                'isChampion'
            ])
        },

        /**
         * Retrieve managers
         */
        async beforeRouteEnter(to, from, next) {
            // TODO: Create mixin and refactor
            // Fetch data from API.
            const params = {};

            if (to.params && to.params.instructor) {
                const { instructorId } = to.params.instructor;
                params.instructorId = instructorId;
            }

            const { data } = await sessions.meetingsList(params);

            next(vm => {
                let paginationBlock = document.querySelector(
                    '.v-data-iterator__actions__range-controls'
                );

                if (data.items && data.meta) {
                    vm.items = data.items;

                    vm.total = data.meta.total;

                    vm.pagination = {
                        page: data.meta.page,
                        rowsPerPage: data.meta.limit
                    };

                    if (to.params && to.params.instructor) {
                        const { fullName, instructorId } = to.params.instructor;
                        vm.filters.instructorId = instructorId;
                    }

                    if (paginationBlock) {
                        if (vm.total <= vm.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }
                }

                vm.$nextTick(() => {
                    vm.routeInitialized = true;
                });
            });
        },

        watch: {
            /**
             * Watch search and set pagination param on change.
             */
            search(value) {
                clearTimeout(this.timeout);

                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);
            },

            /**
             * Watch pagination and update managers data on change.
             */
            pagination: {
                // TODO: create mixin and refactor
                async handler(paginationData) {
                    let paginationBlock = document.querySelector(
                        '.v-data-iterator__actions__range-controls'
                    );

                    if (!this.routeInitialized) {
                        return;
                    }

                    // Start progress bar.
                    this.isLoading = true;

                    // Collect params.
                    const params = {
                        page: paginationData.page,
                        sortBy: this.sortBy,
                        orderBy: this.orderBy
                    };

                    if (
                        paginationData.search &&
                        paginationData.search.length > 0
                    ) {
                        params.search = paginationData.search;
                    }

                    if (this.filters.instructorId) {
                        params.instructorId = this.filters.instructorId;
                    }

                    // Fetch data from API.
                    const { data } = await sessions.meetingsList(params);

                    this.items = data.items;

                    this.total = data.meta.total;

                    if (paginationBlock) {
                        if (this.total <= this.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }

                    // Stop progress bar.
                    this.isLoading = false;
                },
                deep: true
            }
        },

        async created() {
            // Get instructors for filter
            const respInstructors = await managers.instructorsBindedToSession();
            if (respInstructors.ok) {
                this.instructors = respInstructors.data
                    .filter(
                        item =>
                            ![
                                '0c5f2ce61ec9796539a16d769ab3ece2',
                                '5306a007552ead007ded5c89367c11ff'
                            ].includes(item.users_id)
                    )
                    .map(item => ({
                        title: item.users_fullName,
                        value: item.users_id
                    }));
            }
        },

        methods: {
            ...mapActions('sessions', ['readBusinesses']),

            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Get filter param on change filter.
             * @param {object} e
             */
            filtersCallback(e) {
                this.filters[e.name] = e.value;
                this.instructor.fullName;
                this.resetPagination();
            },
            /**
             * Clear filter param on change filter.
             */
            clearAllFilters() {
                this.filters = {};
                this.instructor.fullName;
                this.sortBy = null;
                this.orderBy = null;
                this.resetPagination();
            },

            /**
             * Get  insructor param on change filter.
             * @param {object} e
             */
            instructorFiltersCallback(e) {
                if (!e.value) return;
                this.filters['instructorId'] = e.value;
                this.instructorFullName = e.title;
                this.resetPagination();
            },

            /**
             * Get sort params on change filter.
             * @param {object} e
             */
            sortingCallback(e) {
                if (e.value != null) {
                    this.sortBy = e.name;
                    this.orderBy = e.value ? 'desc' : 'asc';
                } else {
                    this.sortBy = null;
                    this.orderBy = null;
                }

                this.resetPagination();
            },

            /**
             * Change sort items.
             * @param {string} column
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'
    @import "../../../../assets/style/_mixins.styl"

    .createButtonWrapper
        display: flex
        justify-content: flex-end

    .text-left
        text-align: left

    .instructorName
        padding-bottom: 10px
        font-size: 12px

    .description
        position: relative

        &:before
            position: absolute
            top: 8px
            left: 105px
            content: ""
            width: 75%
            height: 1px
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.35) 70%, #022341 100%)

        &:after
            position: absolute
            top: 8px
            left: 0
            content: ""
            width: 20px
            height: 1px
            background: $theme.secondary

    .title
        margin-left: 20px
        padding: 5px 10px
        background-color: $theme.primary
        font-size: 12px !important
        color: $theme.secondary

    >>> .dateHeader
        min-width: 180px
        max-width: 200px

    >>> .SessionActions
        min-width: 210px

    >>> .date-filter-input
        .v-label
        .v-label.v-label--active
            color: $theme.white

        .v-input__icon .v-icon
            color: $theme.white

        &.v-text-field > .v-input__control > .v-input__slot:after
            border-style: none

    .MeditationName
        display: flex
        padding-top: 10px

    .linkAction
        color: $theme.primary
        font-size: 14px

    .filters
        display: flex
        align-items: center
        margin-bottom: 30px

    .sessionHeader
        display: flex
        align-items: center
        justify-content: space-between
        flex-direction: row
        margin: 18px 0 30px

        &.withLogo
            margin: 8px 0 24px

        .titleWrapper h1
            padding-bottom: 0
            margin-bottom: 0
            font-size: remify(40px)
            color: $theme.white
            font-weight: 300

    .businessImageWrapper
        display: flex

    .businessImage
        border-radius: 10px
        overflow: hidden

        img
            width: 65px
            height: 65px

    .clearAllFilters
        opacity: 0.7
        border-radius: 5px

        span
            font-size: 14px

        i
            font-size: 16px

        span
            text-transform: capitalize

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .filters
            flex-wrap: wrap

            > div
                width: 50%
                margin-bottom: 5px
                margin-right: 0 !important

        .clearAllFilters
            span
                font-size: 12px
        .createButtonWrapper
            flex-direction: column

            .btn
                margin-bottom: 10px !important
                width: 100%
</style>
